import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Button, Types } from '@marriott/mi-ui-library';
import { StyledTextEditor } from './TextEditor.styles';
import { TextEditorProps, TextAlignOptions, FontStyleOptions, ColorPanelProps } from './TextEditor.types';
import {
  BoldIcon,
  ItalicIcon,
  LeftAlignIcon,
  CenterAlignIcon,
  RightAlignIcon,
  FontStyleIcon,
  LinkIcon,
  UnlinkIcon,
  FontColorIcon,
  TextHighlightIcon,
} from './icons';
import { ToolbarItem } from './ToolbarItem';
import { ToolbarItemMode } from './ToolbarItem/ToolbarItem.types';
import { TextFormField } from '../../../molecules';
import { isValidDomain, normalizeUrl } from '../../utils/reslink';
import { isValidUrl } from '../../../utils/validations';

const iconMap: Record<string, React.ReactNode> = {
  left: <LeftAlignIcon />,
  center: <CenterAlignIcon />,
  right: <RightAlignIcon />,
};

export const TextEditor: FC<TextEditorProps> = ({
  placeholder,
  maxLength,
  content = '',
  ariaLabel,
  boldAriaLabel,
  italicAriaLabel,
  linkErrorMessage,
  toolbarOptions,
  onChange,
}) => {
  const { alignOptions, fontStyleOptions, linkOptions, fontColors, backgroundColors } = toolbarOptions;

  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkErrorMessage, setShowLinkErrorMessage] = useState(false);
  const [, forceRender] = useState({});

  const editorRef = useRef<HTMLDivElement>(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [3, 4],
        },
        link: false,
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    ],
    immediatelyRender: false,
    content,
    onUpdate({ editor }) {
      onChange(editor.getHTML(), editor.getText());
    },
    editorProps: {
      handleClick(_view, _pos, event) {
        const el = event.target as HTMLElement;
        if (el?.closest('a')) {
          event.preventDefault();
          return true;
        }
        return false;
      },

      handleTextInput(view, _from, _to, text) {
        const len = view.state.doc.textContent.length;
        if (maxLength && len + text.length > maxLength) {
          return true;
        }
        return false;
      },

      handlePaste(view, event) {
        if (!maxLength) return false;

        const clipboardText = event.clipboardData?.getData('text/plain') ?? '';
        if (!clipboardText) return false;

        const currentLength = view.state.doc.textContent.length;
        if (currentLength + clipboardText.length > maxLength) {
          return true;
        }
        return false;
      },

      handleKeyDown(_view, event) {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'u') {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  }) as Editor;

  useEffect(() => {
    if (!editor) return;

    const update = () => forceRender({});

    editor.on('selectionUpdate', update);
    editor.on('transaction', update);

    return () => {
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
    };
  }, [editor]);

  useEffect(() => {
    editor?.commands.setContent(content);
    if (editorRef.current) {
      const contentBox = editorRef.current.querySelector('.ProseMirror');
      contentBox?.setAttribute('role', 'textbox');
      contentBox?.setAttribute('aria-label', ariaLabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, editor]);

  const toggleBold = useCallback(() => {
    editor.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor.chain().focus().toggleItalic().run();
  }, [editor]);

  const handleAlignmentSelect = useCallback(
    (key: string) => {
      const align = key as TextAlignOptions;
      editor.chain().focus().setTextAlign(align).run();
    },
    [editor]
  );

  const handleHeadingSelect = useCallback(
    (key: string) => {
      const selectedKey = key as FontStyleOptions;
      const chain = editor.chain().focus();

      if (selectedKey === FontStyleOptions.PARAGRAPH) {
        chain?.setParagraph().run();
        return;
      }

      const level = Number(selectedKey.substring(1)) as 3 | 4;
      chain?.toggleHeading({ level }).run();
    },
    [editor]
  );

  const handleLinkDialogOpen = useCallback(() => {
    const current = editor.getAttributes('link')?.['href'] ?? '';
    setLinkUrl(current);
  }, [editor]);

  const handleLinkDialogClose = useCallback(() => {
    setLinkUrl('');
  }, []);

  const submitInsertLink = useCallback(
    (close: () => void) => {
      if (!linkUrl?.trim()) {
        close();
      } else if (!isValidDomain(linkUrl) && !isValidUrl(linkUrl)) {
        setShowLinkErrorMessage(true);
      } else {
        const normalizedLinkUrl = normalizeUrl(linkUrl);

        const { from, to } = editor.state.selection;
        const chain = editor.chain().focus();

        if (from === to) {
          chain.insertContent(normalizedLinkUrl);
          chain.setTextSelection({ from, to: from + normalizedLinkUrl.length });
        }
        chain.extendMarkRange('link').setLink({ href: normalizedLinkUrl }).run();
        setLinkUrl('');
        close();
      }
    },
    [editor, linkUrl]
  );

  const handleUnlink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const handleColorSelect = useCallback(
    (color: string, fontColor: boolean, close: () => void) => {
      if (fontColor) {
        editor.chain().focus().setColor(color).run();
      } else {
        editor.chain().focus().setHighlight({ color }).run();
      }
      close();
    },
    [editor]
  );

  const clearHighlight = useCallback(() => {
    editor.chain().focus().unsetHighlight().run();
  }, [editor]);

  const textAlignOptions = alignOptions.map(option => ({
    ...option,
    icon: iconMap[option.key],
  }));

  const linkDialog = (close: () => void) => (
    <div>
      <h4 className="link-dialog-title">{linkOptions?.title}</h4>
      <div className="link-dialog-input">
        <TextFormField
          label={linkOptions?.pathLabel}
          type="text"
          placeholder={linkOptions?.placeholder}
          value={linkUrl}
          ariaLabel={linkOptions?.ariaLabel}
          onChange={e => {
            setLinkUrl(e.target.value);
            showLinkErrorMessage && setShowLinkErrorMessage(false);
          }}
          showErrorMessage={showLinkErrorMessage}
          errorMessage={linkErrorMessage}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              submitInsertLink(close);
            }
          }}
          testId="tiptap-link-input"
          className="m-input-field"
        />
      </div>
      <div className="link-dialog-actions">
        <Button buttonCopy={linkOptions?.cancelLabel} className="m-button-secondary mr-2" callback={close} />
        <Button
          buttonCopy={linkOptions?.submitLabel}
          className="m-button-primary"
          callback={() => submitInsertLink(close)}
        />
      </div>
    </div>
  );

  const colorPalette =
    ({ selectedColor, fontColor, colorsList }: ColorPanelProps) =>
    (close: () => void) => {
      return (
        <>
          <div className="color-palette" role="grid">
            {colorsList.map(color => {
              const isSelected = selectedColor === color;

              return (
                <button
                  key={color}
                  type="button"
                  role="gridcell"
                  aria-label={`Text color ${color}`}
                  className={clsx('color-box', isSelected && 'is-selected')}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorSelect(color, fontColor, close)}
                />
              );
            })}
          </div>
          {!fontColor && (
            <Button
              buttonCopy={'Clear'}
              className="m-button-secondary color-panel-clear-btn"
              callback={() => {
                clearHighlight();
                close();
              }}
            />
          )}
        </>
      );
    };

  if (!editor) {
    return null;
  }

  return (
    <StyledTextEditor ref={editorRef}>
      <div className="d-flex align-items-center p-2 menu">
        <Button
          type={Types.ButtonTypeVariation.Button}
          className={clsx('px-2 py-1 menu-item', editor.isActive('bold') && 'is-active')}
          ariaLabel={boldAriaLabel}
          callback={toggleBold}
        >
          <BoldIcon />
        </Button>
        <Button
          type={Types.ButtonTypeVariation.Button}
          className={clsx('px-2 py-1 menu-item', editor.isActive('italic') && 'is-active')}
          ariaLabel={italicAriaLabel}
          callback={toggleItalic}
        >
          <ItalicIcon />
        </Button>
        <ToolbarItem
          id="alignment"
          ariaLabel="Text Alignment"
          mode={ToolbarItemMode.DROPDOWN}
          icon={<LeftAlignIcon />}
          items={textAlignOptions}
          selectedKey={
            editor?.isActive({ textAlign: TextAlignOptions.LEFT })
              ? TextAlignOptions.LEFT
              : editor?.isActive({ textAlign: TextAlignOptions.RIGHT })
              ? TextAlignOptions.RIGHT
              : editor?.isActive({ textAlign: TextAlignOptions.CENTER })
              ? TextAlignOptions.CENTER
              : ''
          }
          onSelect={handleAlignmentSelect}
          className="ms-2 menu-item"
        />
        <ToolbarItem
          id="heading"
          ariaLabel="Heading"
          mode={ToolbarItemMode.DROPDOWN}
          icon={<FontStyleIcon />}
          items={fontStyleOptions}
          selectedKey={
            editor?.isActive('heading', { level: 3 })
              ? FontStyleOptions.HEADING3
              : editor?.isActive('heading', { level: 4 })
              ? FontStyleOptions.HEADING4
              : FontStyleOptions.PARAGRAPH
          }
          onSelect={handleHeadingSelect}
          className="ms-2 menu-item"
        />
        <ToolbarItem
          id="color"
          ariaLabel="Text Color"
          mode={ToolbarItemMode.PANEL}
          icon={<FontColorIcon />}
          panelClassName="color-panel"
          className="ms-2 menu-item"
          renderPanel={colorPalette({
            selectedColor: editor.getAttributes('textStyle')?.['color'],
            fontColor: true,
            colorsList: fontColors,
          })}
        />
        <ToolbarItem
          id="highlight"
          ariaLabel="Text Highlight"
          mode={ToolbarItemMode.PANEL}
          icon={<TextHighlightIcon />}
          panelClassName="color-panel"
          className="ms-2 menu-item"
          renderPanel={colorPalette({
            selectedColor: editor.getAttributes('highlight')?.['color'],
            fontColor: false,
            colorsList: backgroundColors,
          })}
        />
        <ToolbarItem
          id="link"
          ariaLabel="Link"
          mode={ToolbarItemMode.PANEL}
          icon={<LinkIcon />}
          onOpen={handleLinkDialogOpen}
          onClose={handleLinkDialogClose}
          className="ms-2 menu-item"
          renderPanel={linkDialog}
        />
        <Button
          type={Types.ButtonTypeVariation.Button}
          className={'px-2 py-1 menu-item'}
          ariaLabel={'Remove Link'}
          callback={handleUnlink}
          isDisabled={!editor.isActive('link')}
        >
          <UnlinkIcon />
        </Button>
      </div>
      {placeholder && !editor.getText().length ? <div className="placeholder">{placeholder}</div> : null}
      <EditorContent editor={editor} />
    </StyledTextEditor>
  );
};
